import { z } from 'zod';
import { AssetRef } from './blocks.js';
import { Slug } from './content.js';
const forbiddenMediaKeys = new Set([
    'assetId',
    'cover',
    'format',
    'height',
    'image',
    'media',
    'objectKey',
    'provenance',
    'publicPath',
    'source',
    'src',
    'width',
]);
const semanticStringKeys = new Set([
    'action',
    'alt',
    'answer',
    'attribution',
    'category',
    'copy',
    'description',
    'emphasis',
    'excerpt',
    'heading',
    'href',
    'icon',
    'label',
    'lead',
    'location',
    'name',
    'note',
    'number',
    'price',
    'question',
    'quote',
    'role',
    'slug',
    'statement',
    'subtitle',
    'suffix',
    'text',
    'title',
    'value',
    'year',
]);
const semanticBooleanKeys = new Set(['external', 'flip', 'noindex', 'wide']);
const sourceMediaPath = /^(?:data:image\/.*|blob:.*|file:.*|https?:\/\/\S+\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?|(?:\/|\.{0,2}\/)?(?:[^/\s?#]+\/)*[^/\s?#]+\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?)$/i;
const ReleaseTemplateValue = z.json().superRefine((value, ctx) => {
    const visit = (entry, path) => {
        if (Array.isArray(entry)) {
            entry.forEach((item, index) => visit(item, [...path, index]));
            return;
        }
        if (typeof entry === 'string' && sourceMediaPath.test(entry)) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: 'Release template content must not contain media source data',
            });
            return;
        }
        if (!entry || typeof entry !== 'object')
            return;
        for (const [key, nested] of Object.entries(entry)) {
            const nestedPath = [...path, key];
            if (forbiddenMediaKeys.has(key)) {
                ctx.addIssue({
                    code: 'custom',
                    path: nestedPath,
                    message: 'Release template content must not contain media source data',
                });
            }
            if (semanticStringKeys.has(key) && typeof nested !== 'string') {
                ctx.addIssue({ code: 'custom', path: nestedPath, message: `${key} must be a string` });
            }
            if (semanticBooleanKeys.has(key) && typeof nested !== 'boolean') {
                ctx.addIssue({ code: 'custom', path: nestedPath, message: `${key} must be a boolean` });
            }
            visit(nested, nestedPath);
        }
    };
    visit(value, []);
});
const TemplateObject = z.record(z.string(), ReleaseTemplateValue).superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
        if (forbiddenMediaKeys.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: 'Release template content must not contain media source data',
            });
        }
    }
});
const TemplateList = z.array(TemplateObject);
const PricingTemplatePresentationV1 = z
    .object({
    planKey: z.string().min(1),
    pricePrefix: z.string().min(1).optional(),
    preview: z
        .object({
        includesLimit: z.number().int().positive().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
const PortfolioTemplatePresentationV1 = z
    .object({
    itemKey: z.string().min(1),
    title: z.string().min(1),
    label: z.string().min(1),
})
    .strict();
/**
 * Template-specific editorial content that the normalized page blocks cannot
 * yet express. Media is deliberately excluded: release assets and AssetRefs
 * remain the only materialized-media contract.
 */
export const ReleaseTemplateProjectionV1 = z
    .object({
    schemaVersion: z.literal(1),
    media: z.record(z.string().min(1), AssetRef),
    componentCopy: TemplateObject,
    heroSlides: TemplateList,
    processSteps: TemplateList,
    portfolioCategories: TemplateList,
    portfolioFilters: TemplateList,
    portfolioItems: z.record(z.string().min(1), PortfolioTemplatePresentationV1),
    pricingPackages: z.record(z.string().min(1), PricingTemplatePresentationV1),
    pricingExtras: TemplateList,
    pricingSteps: TemplateList,
    pricingFaqItems: TemplateList,
    aboutLocations: TemplateList,
    aboutFacts: TemplateList,
    aboutPress: TemplateList,
    aboutEducation: TemplateList,
    aboutWork: TemplateObject,
    giftContent: TemplateObject,
    giftSteps: TemplateList,
    giftDetails: TemplateList,
    proposal: TemplateObject,
    wedding: TemplateObject,
    shoots: z.record(Slug, TemplateObject),
    featuredJournalArticle: TemplateObject,
    journalArticles: TemplateList,
    journalFilters: TemplateList,
    relatedJournalArticles: TemplateList,
})
    .strict();
